import { useEffect, useRef, useCallback } from 'react';
import wsService from '../../services/whatsapp/websocket';
import { useNotificationStore } from '../../stores/whatsapp/notificationStore';
import { useChatStore } from '../../stores/whatsapp/chatStore';

const NOTIFICATION_SOUND_URL = '/sounds/notification.mp3';

let audioContext: AudioContext | null = null;

function playNotificationSound() {
  try {
    const audio = new Audio(NOTIFICATION_SOUND_URL);
    audio.volume = 0.5;
    audio.play().catch(() => {
      // Browser blocked autoplay - ignore
    });
  } catch {
    // Fallback: use AudioContext for a simple beep
    try {
      if (!audioContext) audioContext = new AudioContext();
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();
      oscillator.connect(gain);
      gain.connect(audioContext.destination);
      oscillator.frequency.value = 800;
      gain.gain.value = 0.15;
      oscillator.start();
      gain.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.3);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch {
      // No audio support
    }
  }
}

function showBrowserNotification(title: string, body: string, onClick?: () => void) {
  if (!('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;
  if (document.hasFocus()) return;

  const notification = new Notification(title, {
    body,
    icon: '/favicon.ico',
    tag: `contachat-${Date.now()}`,
    silent: true,
  });

  notification.onclick = () => {
    window.focus();
    notification.close();
    onClick?.();
  };

  setTimeout(() => notification.close(), 8000);
}

export function requestNotificationPermission() {
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission();
  }
}

export function useChatNotifications() {
  const { addNotification, soundEnabled, browserEnabled } = useNotificationStore();
  const activeConversationId = useChatStore(s => s.activeConversationId);
  const updateConversation = useChatStore(s => s.updateConversation);
  const addOrUpdateConversation = useChatStore(s => s.addOrUpdateConversation);
  const addMessage = useChatStore(s => s.addMessage);
  const activeConvRef = useRef(activeConversationId);

  useEffect(() => {
    activeConvRef.current = activeConversationId;
  }, [activeConversationId]);

  const handleNewMessage = useCallback(
    (data: any) => {
      const convId = data?.conversation_id;
      const isActive = convId === activeConvRef.current && document.hasFocus();
      const isOwnMessage = data?.sender_type === 'agent';

      // Atualizar conversa na lista (last_message, last_message_at, unread)
      if (convId) {
        let preview = 'Nova mensagem';
        if (data?.content) {
          preview = data.content.length > 100 ? `${data.content.slice(0, 100)}...` : data.content;
        }
        const updates: any = {
          last_message: preview,
          last_message_at: data?.created_at || new Date().toISOString(),
        };
        if (!isActive && !isOwnMessage) {
          updates.unread_count =
            (useChatStore.getState().conversations.find(c => c.id === convId)?.unread_count || 0) +
            1;
        }
        updateConversation(convId, updates);

        // Adicionar mensagem ao store se a conversa esta aberta
        if (isActive && data?.id) {
          const existing = useChatStore.getState().messages[convId] || [];
          if (!existing.some((m: any) => m.id === data.id)) {
            addMessage(convId, {
              id: data.id,
              conversation_id: convId,
              sender_type: data.sender_type || 'client',
              sender_id: data.sender_id,
              content: data.content || '',
              message_type: data.message_type || 'text',
              status: data.status || 'delivered',
              is_internal_note: data.is_internal_note || false,
              created_at: data.created_at || new Date().toISOString(),
              media_url: data.media_url,
              media_filename: data.media_filename,
              media_mime_type: data.media_mime_type,
            });
          }
        }
      }

      // Notificacoes (som, browser, store) — so para mensagens de outros
      if (isActive || isOwnMessage) return;

      const clientName = data?.client_name || data?.sender_name || 'Cliente';
      let preview = 'Nova mensagem';
      if (data?.content) {
        preview = data.content.length > 60 ? `${data.content.slice(0, 60)}...` : data.content;
      }

      addNotification({
        type: 'new_message',
        title: clientName,
        message: preview,
        conversation_id: convId,
        client_name: clientName,
        client_phone: data?.client_phone,
        department: data?.department,
      });

      if (soundEnabled) playNotificationSound();
      if (browserEnabled) showBrowserNotification(clientName, preview);
    },
    [addNotification, soundEnabled, browserEnabled, updateConversation, addMessage],
  );

  const handleNewConversation = useCallback(
    (data: any) => {
      const clientName = data?.client_name || 'Novo cliente';

      // Adicionar conversa nova a lista
      if (data?.conversation_id) {
        addOrUpdateConversation({
          id: data.conversation_id,
          client_id: data.client_id || data.client_phone || '',
          client_name: clientName,
          client_phone: data.client_phone || '',
          client_priority: 'medium',
          status: 'open',
          priority: 'medium',
          last_message: data.message_preview || 'Nova conversa',
          last_message_at: new Date().toISOString(),
          unread_count: 1,
          started_at: new Date().toISOString(),
          department: data.department,
        });
      }

      addNotification({
        type: 'new_conversation',
        title: 'Nova conversa',
        message: `${clientName} iniciou uma conversa`,
        conversation_id: data?.conversation_id,
        client_name: clientName,
        client_phone: data?.client_phone,
        department: data?.department,
      });

      if (soundEnabled) playNotificationSound();
      if (browserEnabled) {
        showBrowserNotification('Nova conversa', `${clientName} iniciou uma conversa`);
      }
    },
    [addNotification, soundEnabled, browserEnabled, addOrUpdateConversation],
  );

  const handleConversationClosed = useCallback(
    (data: any) => {
      const convId = data?.conversation_id;
      if (!convId) return;

      updateConversation(convId, { status: 'closed' });

      const reason = data?.reason;
      const closedBy = data?.closed_by;
      const isInactivity = reason === 'inactivity';
      // Don't notify if the agent is actively viewing the conversation they just closed
      const isActiveConv = convId === activeConvRef.current && document.hasFocus();
      const isAgentClose = reason === 'agent';

      const title = isInactivity ? 'Conversa encerrada por inatividade' : 'Conversa encerrada';
      const message = isInactivity
        ? 'Uma conversa foi encerrada automaticamente por inatividade'
        : `Conversa encerrada por ${closedBy || 'agente'}`;

      // Skip notification if agent is viewing the conversation being closed (own close or inactivity)
      if (isActiveConv && (isAgentClose || isInactivity)) return;

      addNotification({
        type: 'system',
        title,
        message,
        conversation_id: convId,
      });

      if (soundEnabled) playNotificationSound();
      if (browserEnabled) showBrowserNotification(title, message);
    },
    [addNotification, updateConversation, soundEnabled, browserEnabled],
  );

  const handleTransfer = useCallback(
    (data: any) => {
      addNotification({
        type: 'transfer',
        title: 'Conversa transferida',
        message: data?.message || 'Uma conversa foi transferida para você',
        conversation_id: data?.conversation_id,
        client_name: data?.client_name,
        department: data?.department,
      });

      if (soundEnabled) playNotificationSound();
      if (browserEnabled) {
        showBrowserNotification(
          'Conversa transferida',
          data?.message || 'Uma conversa foi transferida para você',
        );
      }
    },
    [addNotification, soundEnabled, browserEnabled],
  );

  useEffect(() => {
    requestNotificationPermission();

    wsService.on('new_message', handleNewMessage);
    wsService.on('new_unassigned_conversation', handleNewConversation);
    wsService.on('transfer', handleTransfer);
    wsService.on('conversation_transferred', handleTransfer);
    wsService.on('conversation_closed', handleConversationClosed);

    return () => {
      wsService.off('new_message', handleNewMessage);
      wsService.off('new_unassigned_conversation', handleNewConversation);
      wsService.off('transfer', handleTransfer);
      wsService.off('conversation_transferred', handleTransfer);
      wsService.off('conversation_closed', handleConversationClosed);
    };
  }, [handleNewMessage, handleNewConversation, handleTransfer, handleConversationClosed]);
}
